<template>
    <div class="col-12">
        <div class="card h-100">
            <div class="card-body p-sm-3 p-md-5 h-100 d-flex flex-column">

                <div v-if="!recording_granted" class="my-auto text-center">
                    <h4 v-if="!loading">{{$t('test_give_permission')}}</h4>
                </div>

                <div v-if="recording_granted" class="text-center">
                    <h4>{{$t('test_title')}}</h4>
                    <template v-if="$md">
                        <template v-if="!$md.mobile()">
                            <p>{{$t('test_desktop')}}</p>
                        </template>
                        <template v-else-if="browser.name === 'ios'">
                            <p>{{$t('test_ios')}}</p>
                            <img src="/img/iphone_landscape.png" width="100%">
                        </template>
                        <template v-else-if="$md.phone()">
                            <p>{{$t('test_phone')}}</p>
                            <img src="/img/android_landscape.png" width="100%">
                        </template>
                        <template v-else-if="$md.tablet()">
                            <p>{{$t('test_tablet')}}</p>
                        </template>
                    </template>
                </div>

                <div class="video-player-container" v-bind:class="{'granted': recording_granted}">
                    <video :style="playing ? 'display: none;' : ''" ref="cameraVideo" autoplay playsinline muted
                           class="video-player"></video>
                    <video :style="playing ? '' : 'display: none;'" ref="playbackVideo" autoplay playsinline
                           class="video-player"></video>
                    <div v-if="recorded && !playing" class="video-player-overlay d-flex justify-content-center" @click="replay(recorded_chunks)"><div class="align-self-center"><i class="fa fa-play-circle-o"></i></div></div>
                </div>

                <div class="text-center mt-1">
                    <sequence-recorder ref="recorder" :player="$refs.playbackVideo" :camera="$refs.cameraVideo"></sequence-recorder>
                    <p v-if="playing" class="m-0">
                        <button class="btn btn-sm btn-success" disabled><i class="fa fa-play"></i> {{$t('global_play')}}</button>
                    </p>

                    <p v-if="recording">{{$t('test_audio')}}</p>
                    <p v-if="recorded">{{$t('test_check_av')}}</p>
                    <p v-if="recorded && !uploading && upload_took_long">{{$t('test_long_duration')}}</p>

                    <div v-show="show_device_choice">
                        <h4 class="mt-3">{{$t('test_choose_source')}}</h4>
                        <div class="form-group row">
                            <label class="col-sm-4 col-form-label" for="videoSelect">{{$t('test_video_source')}}: </label>
                            <div class="col-sm-8">
                                <select class="custom-select" id="videoSelect" ref="videoSelect"></select>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-sm-4 col-form-label" for="audioSelect">{{$t('test_audio_source')}}: </label>
                            <div class="col-sm-8">
                                <select class="custom-select" id="audioSelect" ref="audioSelect"></select>
                            </div>
                        </div>
                    </div>

                    <div class="row justify-content-center text-center">
                        <div class="col p-1">
                            <button v-if="!recording" class="btn btn-outline-dark mt-1 mr-5" @click="show_device_choice = !show_device_choice">
                                <i class="fa fa-arrow-circle-right"></i> {{$t('test_choose_source_button')}}
                            </button>
                        </div>
                        <template v-if="!recorded">
                            <div class="col p-1">
                                <button v-if="!recording" class="btn btn-success btn-lg mt-1" @click="record(5000)" :disabled="!recording_granted">
                                    <i class="fa fa-arrow-circle-right"></i> {{$t('test_start_test')}}
                                </button>
                            </div>
                        </template>
                        <template v-if="recorded">
                            <div class="col p-1">
                                <button v-if="recorded && !uploading" class="btn btn-outline-dark btn" @click="record(5000)"><i class="fa fa-repeat"></i>
                                    {{$t('test_record_again')}}
                                </button>
                            </div>
                            <div class="col p-1">
                                <button v-if="recorded && !uploading" class="btn btn-success" @click="next"><i class="fa fa-arrow-circle-right"></i> {{$t('test_av_ok')}}</button>

                                <button v-if="uploading" class="btn btn-outline-primary" disabled><i class="fa blink fa-circle-o-notch fa-spin fa-fw"></i> {{$t('test_recording_uploading')}}</button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import SequenceRecorder from '@/views/sequence/Recorder';
    import { detect } from 'detect-browser';

    export default {
        components: { SequenceRecorder },
        data() {
            return {
                loading: true,
                browser: detect(),
                constraints: {video: {facingMode: "user"}, audio: true},
                recording: false,
                recorded: false,
                uploading: false,
                upload_completed: undefined,
                upload_took_long: false,
                show_device_choice: false,
                playing: false,
                recording_granted: false,
                recorded_chunks: []
            }
        },
        mounted() {
            setTimeout(()=>{this.loading = false;}, 500);

            this.$refs.audioSelect.onchange = this.getStream;
            this.$refs.videoSelect.onchange = this.getStream;

            this.getStream().then(this.getDevices).then(this.gotDevices);
        },
        methods: {
            /**
             * Replay the recorded chunks from the recorder, and upload it to the server.
             * @param {Array} recordedChunks
             */
            replay(recordedChunks) {
                let url = URL.createObjectURL(recordedChunks);
                let video = this.$refs.playbackVideo;
                video.onended = () => {
                    this.playing = false;
                };
                video.src = url;
                video.load();
                video.onloadeddata = () => {
                    this.playing = true;

                    video.play().catch((error) => {
                        this.playing = false;
                    });
                };
            },
            pause() {
                this.playing = false;
                this.$refs.playbackVideo.pause();
            },
            /**
             * Start recording by delegating it to our record component.
             * @param {Number} ms Duration in milliseconds.
             * @return {Promise}
             */
            record(ms) {
                this.pause();
                this.show_device_choice = false;
                this.upload_completed = undefined;
                this.recording = true;
                this.recorded = false;
                return this.$refs.recorder.record(ms).then((recordedChunks) => {
                    this.recorded_chunks = recordedChunks;
                    this.recorded = true;
                    this.recording = false;
                    this.replay(recordedChunks);
                    this.uploading = true;
                    return this.$api.uploadVideo(recordedChunks, 'testrecording.webm', true).then(() => {
                        this.uploading = false;
                        this.upload_completed = true;
                    }).catch(() => {
                        this.upload_took_long = true;
                        this.uploading = false;
                        this.upload_completed = false;
                    })
                });
            },
            getStream() {
                if(!window.MediaRecorder) {
                    this.onError('MediaRecorder not available.');
                }
                if(!window.navigator || !window.navigator.mediaDevices) {
                    this.onError('navigator or mediaDevices not available.');
                }

                const audioSource = this.$refs.audioSelect.value;
                const videoSource = this.$refs.videoSelect.value;

                const constraints = {
                    audio: {deviceId: audioSource ? {exact: audioSource} : undefined},
                    video: {facingMode: "user", deviceId: videoSource ? {exact: videoSource} : undefined}
                };

                return window.navigator.mediaDevices
                    .getUserMedia(constraints)
                    .then((stream) => {
                        window.stream = stream;

                        this.recording_granted = true;
                        this.$refs.cameraVideo.srcObject = stream;

                    }).catch(function(error) {
                        window.Vue.log(error.message);
                        window.Vue.$router.push({name:'help'});
                    });
            },
            onError(error) {
                this.$state.mediaError = error;
                this.log(error);
                this.$router.push({name:'help'});
            },
            getDevices() {
                // AFAICT in Safari this only gets default devices until gUM is called :/
                return navigator.mediaDevices.enumerateDevices();
            },

             gotDevices(deviceInfos) {
                window.deviceInfos = deviceInfos; // make available to console
                this.log('Available input and output devices:');
                 deviceInfos.forEach(function(device) {
                     if (device.label !== "")
                         Vue.log(device.kind +": " + device.label);
                 });

                for (const deviceInfo of deviceInfos) {
                    const option = document.createElement('option');
                    option.value = deviceInfo.deviceId;
                    if (deviceInfo.kind === 'audioinput') {
                        option.text = deviceInfo.label || `Microphone ${audioSelect.length + 1}`;
                        this.$refs.audioSelect.appendChild(option);
                    } else if (deviceInfo.kind === 'videoinput') {
                        option.text = deviceInfo.label || `Camera ${videoSelect.length + 1}`;
                        this.$refs.videoSelect.appendChild(option);
                    }
                }
            },
            /**
             * Complete the setup and continue.
             */
            next() {
                this.pause();
                this.$router.push({name: 'sequenceBeforeStep'});
            }
        }
    }
</script>
